// src/admin/pages/RtpControl.tsx — FIXED & FINAL VERSION
// All tables list + per-table RTP edit (50-150%), real-time sync, toast, warnings

import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { useForm } from 'react-hook-form';
import { ChevronDown, ChevronUp, Loader2, Search } from 'lucide-react';
import useAdmin from '../hooks/useAdmin';
import { toast } from 'react-hot-toast';
import ConfirmModal from '../../shared/components/ConfirmModal';

interface Table {
  id: string;
  name: string;
  location?: string;
  manager_email?: string;
  current_rtp: number;
  payout_ratio: number;
  created_at: string;
}

interface FormData {
  table_id: string;
  rtp: number;
  payout_ratio: number;
}

const RTP_MIN_PERCENT = 50;
const RTP_MAX_PERCENT = 150;
const RTP_MAX_DECIMAL = RTP_MAX_PERCENT / 100;

export default function RtpControl() {
  const { fetchTables, changeTableRtp, socket, error, setError } = useAdmin();
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<FormData>();
  const [tables, setTables] = useState<Table[]>([]);
  const [filteredTables, setFilteredTables] = useState<Table[]>([]);
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof Table | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [modal, setModal] = useState<{ message: string; callback: () => void } | null>(null);

  const currentRtp = watch('rtp', 92);

  // Load tables
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchTables();
        setTables(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load tables');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fetchTables, setError]);

  // Real-time RTP sync — fixed cleanup
  useEffect(() => {
    if (!socket) return;

    const handleRtpUpdated = (data: { tableId: string; rtp: number }) => {
      const rtpValue = Number(data.rtp);
      const rtpDecimal = rtpValue > RTP_MAX_DECIMAL ? rtpValue / 100 : rtpValue;
      toast(`RTP updated for table ${data.tableId}: ${(rtpDecimal * 100).toFixed(2)}%`, {
        icon: 'ℹ️',
        duration: 5000,
      });

      setTables(prev =>
        prev.map(t =>
          t.id === data.tableId ? { ...t, current_rtp: rtpDecimal } : t
        )
      );
    };

    socket.on('rtp-updated', handleRtpUpdated);

    // Cleanup: remove listener
    return () => {
      socket.off('rtp-updated', handleRtpUpdated);
    };
  }, [socket]); // ← Only socket as dependency

  // Search & Filter
  useEffect(() => {
    setFilteredTables(
      tables.filter(t =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        (t.manager_email && t.manager_email.toLowerCase().includes(search.toLowerCase()))
      )
    );
  }, [search, tables]);

  const handleSort = (column: keyof Table) => {
    setSortAsc(column === sortColumn ? !sortAsc : true);
    setSortColumn(column);
  };

  const handleEditClick = (table: Table) => {
    setSelectedTable(table);
    setValue('table_id', table.id);
    setValue('rtp', table.current_rtp * 100);
    setValue('payout_ratio', table.payout_ratio * 100);
  };

  const onSubmit = (data: FormData) => {
    if (!selectedTable) return;

    const newRtp = data.rtp / 100;
    const newPayout = data.payout_ratio / 100;

    if (data.rtp < RTP_MIN_PERCENT || data.rtp > RTP_MAX_PERCENT) {
      toast.error(`RTP must be between ${RTP_MIN_PERCENT}% and ${RTP_MAX_PERCENT}%`);
      return;
    }

    setModal({
      message: `Update RTP for "${selectedTable.name}"?\nNew RTP: ${data.rtp}% | Payout: ${data.payout_ratio}%`,
      callback: async () => {
        setLoading(true);
        try {
          // Optimistic update
          setTables(prev =>
            prev.map(t =>
              t.id === data.table_id ? { ...t, current_rtp: newRtp, payout_ratio: newPayout } : t
            )
          );

          // FIXED: individual arguments (as per your useAdmin hook)
          await changeTableRtp(data.table_id, data.rtp);

          toast.success('RTP updated successfully!');
          setSelectedTable(null);
          reset();
        } catch (err: any) {
          toast.error(err.message || 'Failed to update RTP');
          setError(err.message);
          // Revert
          fetchTables().then(data => setTables((data || []) as any[]));
        } finally {
          setLoading(false);
          setModal(null);
        }
      },
    });
  };

  return (
    <div className="p-6 space-y-8">
      <h2 className="text-3xl font-bold text-gray-900">RTP Settings & Control</h2>

      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 font-bold">✕</button>
        </div>
      )}

      {/* Inline Edit Form */}
      {selectedTable && (
        <div className="bg-white shadow-xl rounded-xl p-6 border border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">
              Edit RTP for "{selectedTable.name}"
            </h3>
            <button onClick={() => setSelectedTable(null)} className="text-gray-600 hover:text-gray-900">
              Cancel
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                RTP (%) — Current: {(selectedTable.current_rtp * 100).toFixed(1)}%
              </label>
              <input
                type="range"
                min={RTP_MIN_PERCENT}
                max={RTP_MAX_PERCENT}
                step="0.1"
                className="w-full h-2 bg-gray-200 rounded-lg accent-indigo-600"
                {...register('rtp', {
                  required: true,
                  min: { value: RTP_MIN_PERCENT, message: `${RTP_MIN_PERCENT}% min` },
                  max: { value: RTP_MAX_PERCENT, message: `${RTP_MAX_PERCENT}% max` },
                  valueAsNumber: true,
                })}
              />
              <div className="text-center text-2xl font-bold mt-3 text-indigo-700">
                {currentRtp.toFixed(1)}%
              </div>
              {errors.rtp && <p className="mt-1 text-sm text-red-600">{errors.rtp.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payout Ratio (%) — Current: {(selectedTable.payout_ratio * 100).toFixed(1)}%
              </label>
              <input
                type="number"
                step="0.01"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                {...register('payout_ratio', {
                  required: true,
                  min: { value: 0.01, message: 'Min 0.01' },
                  max: { value: 100, message: 'Max 100' },
                  valueAsNumber: true,
                })}
              />
              {errors.payout_ratio && <p className="mt-1 text-sm text-red-600">{errors.payout_ratio.message}</p>}
            </div>

            <div className="md:col-span-2 flex justify-end gap-4">
              <button
                type="button"
                onClick={() => setSelectedTable(null)}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-8 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition shadow disabled:opacity-50 ${
                  loading ? 'cursor-not-allowed' : ''
                }`}
              >
                {loading ? (
                  <span className="flex items-center">
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Updating...
                  </span>
                ) : (
                  'Save RTP Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tables List */}
      <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-800">All Tables</h3>
          <div className="relative w-64">
            <Search className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name or manager..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="animate-spin mx-auto text-indigo-600" size={40} />
            <p className="mt-4 text-gray-600">Loading tables...</p>
          </div>
        ) : filteredTables.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            No tables found {search ? `matching "${search}"` : ''}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <tr>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort('name')}>
                    Name {sortColumn === 'name' && (sortAsc ? <ChevronUp size={16} className="inline" /> : <ChevronDown size={16} className="inline" />)}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort('manager_email')}>
                    Manager {sortColumn === 'manager_email' && (sortAsc ? <ChevronUp size={16} className="inline" /> : <ChevronDown size={16} className="inline" />)}
                  </th>
                  <th className="p-4 text-center cursor-pointer" onClick={() => handleSort('current_rtp')}>
                    RTP % {sortColumn === 'current_rtp' && (sortAsc ? <ChevronUp size={16} className="inline" /> : <ChevronDown size={16} className="inline" />)}
                  </th>
                  <th className="p-4 text-center cursor-pointer" onClick={() => handleSort('payout_ratio')}>
                    Payout % {sortColumn === 'payout_ratio' && (sortAsc ? <ChevronUp size={16} className="inline" /> : <ChevronDown size={16} className="inline" />)}
                  </th>
                  <th className="p-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTables.map(table => (
                  <tr key={table.id} className="border-b hover:bg-gray-50 transition">
                    <td className="p-4 font-medium">{DOMPurify.sanitize(table.name || "")}</td>
                    <td className="p-4 text-gray-600">{table.manager_email || 'Unassigned'}</td>
                    <td className="p-4 text-center font-bold" style={{
                      color: table.current_rtp >= 0.95 ? '#10b981' :
                             table.current_rtp >= 0.90 ? '#f59e0b' :
                             '#ef4444'
                    }}>
                      {(table.current_rtp * 100).toFixed(1)}%
                    </td>
                    <td className="p-4 text-center text-gray-700">{(table.payout_ratio * 100).toFixed(1)}%</td>
                    <td className="p-4">
                      <button
                        onClick={() => handleEditClick(table)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
                      >
                        Edit RTP
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <ConfirmModal
          isOpen={!!modal}
          message={modal.message}
          onConfirm={modal.callback}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
}
